import { ROLE_LABELS, TEAM_LEVELS, TEAM_LEVEL_LABELS, isEscalation, type TeamMember } from '@easyhelp/shared';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useId, useState } from 'react';
import { useTeamMembers } from '../../api/team';
import { workloadLabel } from '../impostazioni/team/workload';
import { ESCALATION_LABEL } from './assignment-text';

/** Che cosa sceglie il dialogo: il primo responsabile, un nuovo responsabile o un collaboratore. */
export type AssignMode = 'assegna' | 'cambia' | 'collaboratore';

const TITLES: Record<AssignMode, string> = {
  assegna: 'Assegna il ticket',
  cambia: 'Cambia responsabile',
  collaboratore: 'Aggiungi collaboratore',
};

const CONFIRM_LABELS: Record<AssignMode, string> = {
  assegna: 'Assegna',
  cambia: 'Cambia responsabile',
  collaboratore: 'Aggiungi',
};

const INTRO: Record<AssignMode, string> = {
  assegna: 'Il primo assegnato diventa responsabile e il ticket passa a In lavorazione.',
  cambia:
    'Il nuovo responsabile prende il posto di quello attuale, che esce dal ticket; un collaboratore scelto diventa responsabile.',
  collaboratore: 'Il collaboratore lavora il ticket accanto al responsabile.',
};

export interface AssignMemberDialogProps {
  open: boolean;
  mode: AssignMode;
  code: string;
  /** Membri già sul ticket (responsabile e collaboratori): non si propongono. */
  excludedIds: readonly string[];
  busy: boolean;
  error: string | null;
  onConfirm: (member: TeamMember) => void;
  onClose: () => void;
}

/**
 * Scelta di un membro **attivo** del team (PRD-004 US-3), a scelta singola (`radiogroup`):
 * raggruppato per livello come la schermata Team (Primo livello · Analisti, Secondo livello ·
 * Sviluppo), con il carico di ciascuno.
 * Un membro del secondo livello è etichettato come escalation (DEC-004). Chi riceve l'assegnazione
 * riceve la notifica email con codice, committente, priorità e link, mai il testo della segnalazione.
 */
export function AssignMemberDialog({
  open,
  mode,
  code,
  excludedIds,
  busy,
  error,
  onConfirm,
  onClose,
}: AssignMemberDialogProps) {
  const titleId = useId();
  const { data, isPending, isError, error: loadError } = useTeamMembers();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const candidates = (data?.members ?? []).filter((member) => !excludedIds.includes(member.id));
  const selected = candidates.find((member) => member.id === selectedId) ?? null;
  const close = () => {
    if (!busy) onClose();
  };

  return (
    <Dialog open={open} onClose={close} aria-labelledby={titleId} fullWidth maxWidth="xs">
      <DialogTitle id={titleId}>
        {TITLES[mode]} · {code}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3}>
          <DialogContentText>
            {INTRO[mode]} Riceve una notifica email con codice, committente, priorità e link al ticket (nessuna email se
            assegni te stesso).
          </DialogContentText>
          {isPending && (
            <Stack direction="row" spacing={2} alignItems="center" role="status">
              <CircularProgress size={16} />
              <Typography variant="body2">Caricamento del team…</Typography>
            </Stack>
          )}
          {isError && <Alert severity="error">{loadError.message}</Alert>}
          {data && candidates.length === 0 && (
            <Alert severity="info">Nessun altro membro attivo del team da assegnare.</Alert>
          )}
          {candidates.length > 0 && (
            <RadioGroup
              name={`${titleId}-membro`}
              aria-label="Membro del team"
              value={selectedId ?? ''}
              onChange={(event) => setSelectedId(event.target.value)}
            >
              {TEAM_LEVELS.map((level) => {
                const members = candidates.filter((member) => member.level === level);
                if (members.length === 0) return null;
                const groupId = `${titleId}-${level}`;
                return (
                  <Box key={level} role="group" aria-labelledby={groupId} sx={{ mb: 2 }}>
                    <Typography id={groupId} variant="overline" component="p" color="text.secondary">
                      {TEAM_LEVEL_LABELS[level]}
                    </Typography>
                    {members.map((member) => (
                      <FormControlLabel
                        key={member.id}
                        value={member.id}
                        control={<Radio size="small" />}
                        sx={{ display: 'flex', alignItems: 'flex-start', mx: 0, py: 0.5 }}
                        label={
                          <Stack spacing={0.5} sx={{ pt: 1 }}>
                            <Stack direction="row" spacing={2} alignItems="center" component="span">
                              <span>{member.name}</span>
                              {isEscalation(member.level) && (
                                <Chip size="small" color="warning" variant="outlined" label={ESCALATION_LABEL} />
                              )}
                            </Stack>
                            <Typography component="span" variant="body2" color="text.secondary">
                              {ROLE_LABELS[member.role]} · {workloadLabel(member.openTicketCount)}
                            </Typography>
                          </Stack>
                        }
                      />
                    ))}
                  </Box>
                );
              })}
            </RadioGroup>
          )}
          {selected && isEscalation(selected.level) && (
            <Alert severity="warning" role="note">
              {selected.name} è del secondo livello: l’assegnazione è l’escalation allo Sviluppo.
            </Alert>
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={busy}>
          Annulla
        </Button>
        <Button
          variant="contained"
          disabled={!selected || busy}
          onClick={() => {
            if (selected) onConfirm(selected);
          }}
        >
          {selected && isEscalation(selected.level)
            ? `${CONFIRM_LABELS[mode]} · ${ESCALATION_LABEL.toLowerCase()}`
            : CONFIRM_LABELS[mode]}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
